import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL as _BASE } from "../../../../config";

const API_BASE_URL = `${_BASE}/tutorials`;

const Tutorials = () => {
  const [tutorials, setTutorials] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    link: "",
    description: "",
    readTime: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(API_BASE_URL);
      setTutorials(response.data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to fetch tutorials"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    if (
      !formData.title ||
      !formData.link ||
      !formData.description ||
      !formData.readTime
    ) {
      setError("All fields are required");
      setIsLoading(false);
      return;
    }

    try {
      let response;
      if (editingId) {
        response = await axios.put(`${API_BASE_URL}/${editingId}`, formData);
      } else {
        response = await axios.post(API_BASE_URL, formData);
      }

      setSuccess(
        editingId
          ? "Tutorial updated successfully!"
          : "Tutorial added successfully!"
      );
      fetchTutorials();

      setFormData({
        title: "",
        link: "",
        description: "",
        readTime: "",
      });
      setEditingId(null);

      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          (editingId ? "Failed to update tutorial" : "Failed to add tutorial")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (tutorial) => {
    setFormData({
      title: tutorial.title,
      link: tutorial.link,
      description: tutorial.description,
      readTime: tutorial.readTime,
    });
    setEditingId(tutorial._id || tutorial.id);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this tutorial?")) {
      setIsLoading(true);
      try {
        await axios.delete(`${API_BASE_URL}/${id}`);
        setSuccess("Tutorial deleted successfully!");
        fetchTutorials();

        if (editingId === id) {
          setFormData({
            title: "",
            link: "",
            description: "",
            readTime: "",
          });
          setEditingId(null);
        }

        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to delete tutorial"
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const cancelEdit = () => {
    setFormData({
      title: "",
      link: "",
      description: "",
      readTime: "",
    });
    setEditingId(null);
  };

  const containerStyle = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    padding: "24px",
    margin: "16px 0",
  };

  const rowStyle = {
    display: "flex",
    flexWrap: "wrap",
    margin: "0 -12px",
  };

  const colStyle = {
    padding: "0 12px",
    flex: "0 0 auto",
    width: "100%",
  };

  const colMd5Style = {
    ...colStyle,
    flex: "0 0 auto",
    width: "41.66666667%",
  };

  const colMd7Style = {
    ...colStyle,
    flex: "0 0 auto",
    width: "58.33333333%",
  };

  const headingStyle = {
    fontSize: "1.25rem",
    marginBottom: "16px",
    fontWeight: "500",
    lineHeight: "1.2",
  };

  const alertStyle = {
    padding: "12px 16px",
    marginBottom: "16px",
    border: "1px solid transparent",
    borderRadius: "4px",
  };

  const alertDangerStyle = {
    ...alertStyle,
    color: "#721c24",
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
  };

  const alertSuccessStyle = {
    ...alertStyle,
    color: "#155724",
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
  };

  const formGroupStyle = {
    marginBottom: "16px",
  };

  const labelStyle = {
    display: "inline-block",
    marginBottom: "8px",
  };

  const inputStyle = {
    display: "block",
    width: "100%",
    padding: "8px 12px",
    fontSize: "1rem",
    lineHeight: "1.5",
    color: "#495057",
    backgroundColor: "#fff",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
  };

  const textareaStyle = {
    ...inputStyle,
    height: "auto",
    resize: "vertical",
  };

  const buttonStyle = {
    display: "inline-block",
    fontWeight: "400",
    textAlign: "center",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    userSelect: "none",
    border: "1px solid transparent",
    padding: "8px 16px",
    fontSize: "1rem",
    lineHeight: "1.5",
    borderRadius: "4px",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    cursor: "pointer",
  };

  const buttonPrimaryStyle = {
    ...buttonStyle,
    color: "#fff",
    backgroundColor: "#1664C0",
    borderColor: "#1664C0",
  };

  const buttonOutlineSecondaryStyle = {
    ...buttonStyle,
    color: "#6c757d",
    backgroundColor: "transparent",
    borderColor: "#6c757d",
  };

  const buttonOutlinePrimaryStyle = {
    ...buttonStyle,
    color: "#1664C0",
    backgroundColor: "transparent",
    borderColor: "#1664C0",
  };

  const buttonOutlineDangerStyle = {
    ...buttonStyle,
    color: "#dc3545",
    backgroundColor: "transparent",
    borderColor: "#dc3545",
  };

  const buttonSmallStyle = {
    ...buttonStyle,
    padding: "4px 8px",
    fontSize: "0.875rem",
    lineHeight: "1.5",
    borderRadius: "3px",
  };

  const flexGapStyle = {
    display: "flex",
    gap: "8px",
  };

  const cardStyle = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minWidth: "0",
    wordWrap: "break-word",
    backgroundColor: "#fff",
    backgroundClip: "border-box",
    border: "1px solid rgba(0, 0, 0, 0.125)",
    borderRadius: "4px",
    marginBottom: "16px",
  };

  const cardBodyStyle = {
    flex: "1 1 auto",
    padding: "16px",
  };

  const cardTitleStyle = {
    marginBottom: "8px",
    fontSize: "1rem",
    fontWeight: "500",
    lineHeight: "1.2",
  };

  const cardTextStyle = {
    color: "#6c757d",
    fontSize: "0.875rem",
    marginBottom: "8px",
  };

  const badgeStyle = {
    display: "inline-block",
    padding: "4px 8px",
    fontSize: "0.75rem",
    fontWeight: "700",
    lineHeight: "1",
    textAlign: "center",
    whiteSpace: "nowrap",
    verticalAlign: "baseline",
    borderRadius: "4px",
    backgroundColor: "#d1ecf1",
    color: "#0c5460",
  };

  return (
    <div style={containerStyle}>
      <div style={rowStyle}>
        <div style={colMd5Style}>
          <h5 style={headingStyle}>
            {editingId ? "Edit Tutorial" : "Add New Tutorial"}
          </h5>
          {error && (
            <div style={alertDangerStyle} role="alert">
              {error}
            </div>
          )}
          {success && (
            <div style={alertSuccessStyle} role="alert">
              {success}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div style={formGroupStyle}>
              <label htmlFor="title" style={labelStyle}>
                Title
              </label>
              <input
                type="text"
                style={inputStyle}
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter tutorial title"
              />
            </div>
            <div style={formGroupStyle}>
              <label htmlFor="link" style={labelStyle}>
                Link
              </label>
              <input
                type="url"
                style={inputStyle}
                id="link"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                placeholder="https://example.com/tutorial"
              />
            </div>
            <div style={formGroupStyle}>
              <label htmlFor="description" style={labelStyle}>
                Description
              </label>
              <textarea
                style={textareaStyle}
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter tutorial description"
                rows="3"
              ></textarea>
            </div>
            <div style={formGroupStyle}>
              <label htmlFor="readTime" style={labelStyle}>
                Duration
              </label>
              <input
                type="text"
                style={inputStyle}
                id="readTime"
                name="readTime"
                value={formData.readTime}
                onChange={handleInputChange}
                placeholder="e.g. 15 mins"
              />
            </div>
            <div style={flexGapStyle}>
              <button
                type="submit"
                style={{
                  ...buttonPrimaryStyle,
                  opacity: isLoading ? "0.65" : "1",
                }}
                disabled={isLoading}
              >
                {isLoading
                  ? editingId
                    ? "Updating..."
                    : "Adding..."
                  : editingId
                  ? "Update Tutorial"
                  : "Add Tutorial"}
              </button>
              {editingId && (
                <button
                  type="button"
                  style={{
                    ...buttonOutlineSecondaryStyle,
                    opacity: isLoading ? "0.65" : "1",
                  }}
                  onClick={cancelEdit}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div style={colMd7Style}>
          <h5 style={headingStyle}>Tutorials List</h5>
          {isLoading && tutorials.length === 0 ? (
            <p>Loading tutorials...</p>
          ) : (
            <div>
              {tutorials.length === 0 ? (
                <p>No tutorials found. Add your first tutorial!</p>
              ) : (
                tutorials.map((tutorial) => (
                  <div key={tutorial._id || tutorial.id} style={cardStyle}>
                    <div style={cardBodyStyle}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <h6 style={cardTitleStyle}>{tutorial.title}</h6>
                        <span style={badgeStyle}>{tutorial.readTime}</span>
                      </div>
                      <p style={cardTextStyle}>{tutorial.description}</p>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <a
                          href={tutorial.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            ...buttonOutlinePrimaryStyle,
                            ...buttonSmallStyle,
                          }}
                        >
                          View Tutorial
                        </a>
                        <div style={flexGapStyle}>
                          <button
                            onClick={() => handleEdit(tutorial)}
                            style={{
                              ...buttonSmallStyle,
                              color: "#1664C0",
                              backgroundColor: "transparent",
                              border: "none",
                              opacity: isLoading ? "0.65" : "1",
                            }}
                            disabled={isLoading}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDelete(tutorial._id || tutorial.id)
                            }
                            style={{
                              ...buttonOutlineDangerStyle,
                              ...buttonSmallStyle,
                              opacity: isLoading ? "0.65" : "1",
                            }}
                            disabled={isLoading}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tutorials;
